import type { IpdTaskDisplayStatus, IpdTaskView, IpdWardTaskPatient, IpdWardTaskWindow } from '@his/shared';
import { displayStatus, taskIsPending } from './record-task';

export const WARD_NEXT_HORIZON_MINUTES = 120;
export const WARD_BUCKETS = ['overdue', 'due', 'next'] as const;
export type WardBucket = (typeof WARD_BUCKETS)[number];

export interface WardBoardEntry {
  task: IpdTaskView;
  status: IpdTaskDisplayStatus;
  lapsed: boolean;
}

export interface WardBoardPatient {
  patient: IpdWardTaskPatient;
  entries: WardBoardEntry[];
}

export interface WardBoard {
  groups: Record<WardBucket, WardBoardPatient[]>;
  counts: Record<WardBucket, number>;
}

export function compareBeds(a: string | null | undefined, b: string | null | undefined): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

export function wardBucketOf(
  task: Pick<IpdTaskView, 'storedStatus' | 'scheduledAt' | 'taskKind'>,
  nowMs: number,
  horizonMinutes: number = WARD_NEXT_HORIZON_MINUTES,
): { bucket: WardBucket; status: IpdTaskDisplayStatus; lapsed: boolean } | null {
  if (!taskIsPending(task)) return null;
  const status = displayStatus(task, nowMs);
  if (status === 'overdue' || status === 'missed') return { bucket: 'overdue', status, lapsed: status === 'missed' };
  if (status === 'due') return { bucket: 'due', status, lapsed: false };
  if (status === 'upcoming' && Date.parse(task.scheduledAt) <= nowMs + horizonMinutes * 60_000) return { bucket: 'next', status, lapsed: false };
  return null;
}

function unknownPatient(admissionId: string): IpdWardTaskPatient {
  return { admissionId, admissionNumber: '', bedCode: null, patientId: '', patientName: '', mrn: '' };
}

export function wardBoard(window: Pick<IpdWardTaskWindow, 'patients' | 'data'> | null | undefined, nowMs: number, horizonMinutes: number = WARD_NEXT_HORIZON_MINUTES): WardBoard {
  const groups: Record<WardBucket, WardBoardPatient[]> = { overdue: [], due: [], next: [] };
  const counts: Record<WardBucket, number> = { overdue: 0, due: 0, next: 0 };
  if (!window) return { groups, counts };
  const patients = new Map(window.patients.map((patient) => [patient.admissionId, patient]));
  const buckets: Record<WardBucket, Map<string, WardBoardPatient>> = { overdue: new Map(), due: new Map(), next: new Map() };
  for (const task of window.data) {
    const placed = wardBucketOf(task, nowMs, horizonMinutes);
    if (!placed) continue;
    const bucket = buckets[placed.bucket];
    let group = bucket.get(task.admissionId);
    if (!group) {
      group = { patient: patients.get(task.admissionId) ?? unknownPatient(task.admissionId), entries: [] };
      bucket.set(task.admissionId, group);
    }
    group.entries.push({ task, status: placed.status, lapsed: placed.lapsed });
    counts[placed.bucket] += 1;
  }
  for (const name of Object.keys(buckets) as WardBucket[]) {
    const list = [...buckets[name].values()];
    for (const group of list) group.entries.sort((a, b) => Date.parse(a.task.scheduledAt) - Date.parse(b.task.scheduledAt) || a.task.id.localeCompare(b.task.id));
    list.sort((a, b) => compareBeds(a.patient.bedCode, b.patient.bedCode) || a.patient.patientName.localeCompare(b.patient.patientName) || a.patient.admissionId.localeCompare(b.patient.admissionId));
    groups[name] = list;
  }
  return { groups, counts };
}
